// Builders for the /exceldata page: the /api/data/* address Excel's Power Query
// reads, and an Advanced Editor (M) query that also sets the column types. The
// address format is the webserver's `data::sheet_data` contract.

export type DataKind = "current" | "binary" | "trend";
export type DataFormat = "csv" | "json";

export interface DataQuery {
  kind: DataKind;
  config: string;
  /** In column order. */
  tags: string[];
  /** Site local time, `YYYY-MM-DD HH:MM[:SS]`; null reads the live buffer.
   *  Ignored for `current`. */
  range: { start: string; end: string } | null;
  /** Seconds between rows; null or 0 keeps every sample. Ignored for `current`. */
  step: number | null;
  format: DataFormat;
}

/** Longest binary range one request may ask for (the server's limit). */
export const MAX_BINARY_RANGE_DAYS = 31;

/** `<input type="datetime-local">` value (`YYYY-MM-DDTHH:MM`) → the server's
 *  `YYYY-MM-DD HH:MM`. */
export function fromDatetimeLocal(value: string): string {
  return value.replace("T", " ");
}

/** A Date as a `datetime-local` value in the browser's clock. */
export function toDatetimeLocal(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** The address for `q` under `origin`. Tags keep their `(AVG)` parentheses
 *  readable; times encode their space as %20. */
export function buildDataUrl(origin: string, q: DataQuery): string {
  const params = [
    `config=${encodeURIComponent(q.config)}`,
    `tags=${q.tags.map(encodeURIComponent).join(",")}`,
  ];
  if (q.kind !== "current") {
    if (q.range) {
      params.push(`start=${encodeURIComponent(q.range.start)}`, `end=${encodeURIComponent(q.range.end)}`);
    }
    if (q.step && q.step > 0) params.push(`step=${Math.floor(q.step)}`);
  }
  if (q.format === "json") params.push("format=json");
  return `${origin}/api/data/${q.kind}?${params.join("&")}`;
}

/** An M string literal. */
function mString(text: string): string {
  return `"${text.replace(/"/g, '""')}"`;
}

/** Column names the server returns for `q`, in order. */
export function dataColumns(q: DataQuery): string[] {
  return ["Time", ...(q.kind === "current" ? ["State"] : []), ...q.tags];
}

/**
 * A Power Query (M) query for the Advanced Editor of a Blank Query. Beyond what
 * From Web does, it types the columns with the en-US culture, so the server's
 * '.' decimals and ISO times read correctly whatever Excel's own locale.
 */
export function buildPowerQueryM(url: string, q: DataQuery): string {
  const types = dataColumns(q)
    .map((c) => `{${mString(c)}, ${c === "Time" ? "type datetime" : c === "State" ? "type text" : "type number"}}`)
    .join(", ");
  const source =
    q.format === "json"
      ? [
          `    Source = Json.Document(Web.Contents(${mString(url)})),`,
          `    Rows = Table.FromRows(Source[rows], Source[columns]),`,
        ]
      : [
          `    Source = Csv.Document(Web.Contents(${mString(url)}), [Delimiter = ",", Encoding = 65001, QuoteStyle = QuoteStyle.Csv]),`,
          `    Rows = Table.PromoteHeaders(Source, [PromoteAllScalars = true]),`,
        ];
  return [
    "let",
    ...source,
    `    Typed = Table.TransformColumnTypes(Rows, {${types}}, "en-US")`,
    "in",
    "    Typed",
  ].join("\n");
}

/** Why `q` cannot be requested yet, as a resource id; null when it can. */
export function queryProblem(q: DataQuery): string | null {
  if (!q.config || q.tags.length === 0) return "TIGER_XL_NEED";
  if (q.kind !== "current" && q.range) {
    const start = Date.parse(q.range.start.replace(" ", "T"));
    const end = Date.parse(q.range.end.replace(" ", "T"));
    if (!(end > start)) return "TIGER_XL_BAD_RANGE";
    if (q.kind === "binary" && end - start > MAX_BINARY_RANGE_DAYS * 24 * 3600 * 1000) {
      return "TIGER_XL_LONG_RANGE";
    }
  }
  return null;
}
